import axios from 'axios';
import { format } from 'date-fns';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  getCreatePlaylistAudioLink,
  getCreatePlaylistLink,
  getDeletePlaylistAudioLink,
  getDeletePlaylistImageLink,
  getDeletePlaylistLink,
  getFetchPlaylistLink,
  getMovePlaylistAudioLink,
  getPatchPlaylistLink,
  getPlaylistImageUploadStatusLink,
  getPlaylistsListLink,
  getUpdatePlaylistAudioLink,
  getUploadPlaylistImageLink,
} from 'app/store/Playlists/selectors';
import { dateFormats } from 'app/constants';
import {
  AddPlaylistAudiosResponse,
  CreatePlaylistResponse,
  GetPlaylistResponse,
  GetPlaylistsListResponse,
  Playlist,
  PlaylistAudio,
  PlaylistAudiosData,
  UpdatePlaylistData,
  UpdatePlaylistResponse,
} from 'types/playlists.types';
import { buildQueryUrl } from 'app/utils/utils';
import { Link } from 'types/api.types';
import { ReorderBody } from 'app/components/DraggableListRow/types';
import { createAsyncThunk, uploadImageHandler } from 'app/store/utils';
import { ApiErrorCodeTypes, ImageStatusType } from 'types/enums';
import { UploadImageRequestData } from 'types/general.types';
import { FetchUploadImageStatusResponse } from 'types/profile';

export const createPlaylistEffect = createAsyncThunk<
  Omit<Playlist, 'playlistAudios'>,
  { audioCount: number; songGuid?: string }
>(
  'playlists/createPlaylist',
  async (_data, { getState, dispatch, rejectWithValue }) => {
    const link = getCreatePlaylistLink(getState());

    try {
      if (!link) {
        throw new Error('Create playlist link not found');
      }

      const title = `New Playlist - ${format(
        new Date(),
        dateFormats.LIST_FORMAT
      )}`;
      const response = await axios.request<CreatePlaylistResponse>({
        url: link.href,
        method: link.method,
        data: {
          title,
        },
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Playlist created' }));
      return response.data.response.playlist.item;
    } catch (e: any) {
      if (e?.response?.status === 403) {
        return rejectWithValue({
          code: 403,
          message: 'Limit has been reached',
        });
      }

      dispatch(
        addAlertAction({
          message: 'Failed to create Playlist. Please try again',
        })
      );

      return rejectWithValue(e);
    }
  }
);

export const addPlaylistAudiosEffect = createAsyncThunk<
  PlaylistAudio[],
  PlaylistAudiosData
>('playlists/addPlaylistAudios', async (data, { getState, dispatch }) => {
  const link = data.link || getCreatePlaylistAudioLink(getState());

  try {
    if (!link) {
      throw new Error('Create playlist audio link not found');
    }

    const response = await axios.request<AddPlaylistAudiosResponse>({
      url: link.href,
      method: link.method,
      data: {
        inFront: true,
        playlistAudios: Object.values(data.audios).map((el) => ({
          audioGuid: el.audioGuid,
          songGuid: el.songGuid,
        })),
      },
      withCredentials: true,
    });
    return response.data.response.playlistAudio.items;
  } catch (e: any) {
    const err = e?.response?.data?.error;
    if (
      err?.statusCode === 403 &&
      err.errorCode === ApiErrorCodeTypes.PLAYLIST_AUDIO_LIMIT_REACHED
    ) {
      dispatch(
        addAlertAction({
          message:
            'The maximum number of audio files reached for the selected playlist',
        })
      );
    } else {
      dispatch(
        addAlertAction({
          message: 'Failed to add audios to Playlist. Please try again',
        })
      );
    }

    throw e;
  }
});

export const fetchPlaylistsEffect = createAsyncThunk(
  'playlists/fetchPlaylistsList',
  async (
    data: { next?: Link; limit?: number } | undefined = {},
    { getState, dispatch }
  ) => {
    const { next, limit } = data;
    const link = getPlaylistsListLink(getState());

    try {
      if (!link) {
        throw new Error('Get playlists link not found');
      }

      const url =
        next?.href ||
        buildQueryUrl(link.href, { limit: limit ? limit.toString() : '' });

      const response = await axios.get<GetPlaylistsListResponse>(url, {
        withCredentials: true,
      });

      const { items, _links } = response.data.response.playlist;
      return { items, _links };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Something went wrong. Please try again' })
      );
      throw e;
    }
  }
);

export const fetchPlaylistEffect = createAsyncThunk(
  'playlists/fetchPlaylist',
  async (data: { playlistGuid: string }, { getState, dispatch }) => {
    const link = getFetchPlaylistLink(getState());

    try {
      if (!link) {
        throw new Error('Fetch playlist link not found');
      }

      const response = await axios.request<GetPlaylistResponse>({
        url: link.href.replace('{playlistGuid}', data.playlistGuid),
        method: link.method,
        withCredentials: true,
      });

      return response.data.response.playlist.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to load Playlist. Please try again',
        })
      );
      throw e;
    }
  }
);

export const deletePlaylistAudioEffect = createAsyncThunk(
  'playlists/deletePlaylistAudio',
  async (data: { audioGuid: string }, { getState, dispatch }) => {
    const link = getDeletePlaylistAudioLink(getState());
    try {
      if (!link) {
        throw new Error('Fetch playlist link not found');
      }

      await axios.request<GetPlaylistResponse>({
        url: link.href.replace('{playlistAudioGuid}', data.audioGuid),
        method: link.method,
        withCredentials: true,
      });

      dispatch(
        addAlertAction({
          message: 'Audio removed',
        })
      );
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to remove Audio. Please try again',
        })
      );
      throw e;
    }
  }
);

export const patchPlaylistEffect = createAsyncThunk(
  'playlists/patchPlaylist',
  async (data: UpdatePlaylistData, { getState, dispatch }) => {
    const link = getPatchPlaylistLink(getState());

    try {
      if (!link) {
        throw new Error('Update playlist link not found');
      }

      const response = await axios.request<UpdatePlaylistResponse>({
        url: link.href,
        data: {
          ...data,
          description: data.description === '' ? null : data.description,
        },
        method: link.method,
        withCredentials: true,
      });

      return response.data.response.playlist.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to update Playlist. Please try again',
        })
      );
      throw e;
    }
  }
);

export const movePlaylistAudioEffect = createAsyncThunk(
  'playlists/movePlaylistAudio',
  async (
    data: {
      reorder: ReorderBody;
      reordered: PlaylistAudio[];
    },
    { getState, dispatch }
  ) => {
    const link = getMovePlaylistAudioLink(getState());
    try {
      if (!link) {
        throw new Error('Update audio order link not found');
      }

      await axios.request({
        url: link.href,
        method: link.method,
        data: {
          playlistAudioGuid: data.reorder.guid,
          afterPlaylistAudioGuid: data.reorder.afterGuid,
        },
        withCredentials: true,
      });

      dispatch(addAlertAction({ message: 'Audios reordered' }));
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Audios reorder failed. Please try again',
        })
      );
    }
  }
);

export const deletePlaylistEffect = createAsyncThunk(
  'playlists/deletePlaylist',
  async (data: { playlistGuid: string }, { getState, dispatch }) => {
    const link = getDeletePlaylistLink(getState());

    try {
      if (!link) {
        throw new Error('Delete playlist link not found');
      }

      await axios.request({
        url: link.href.replace('{playlistGuid}', data.playlistGuid),
        method: link.method,
        withCredentials: true,
      });

      dispatch(
        addAlertAction({
          message: 'Playlist deleted',
        })
      );
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to delete Playlist. Please try again',
        })
      );
      throw e;
    }
  }
);

export const updatePlaylistAudioPropsEffect = createAsyncThunk(
  'playlists/updatePlaylistAudioProps',
  async (
    data: { note?: string; title?: string; playlistAudioGuid: string },
    { getState, dispatch }
  ) => {
    const { playlistAudioGuid, note, title } = data;
    const link = getUpdatePlaylistAudioLink(getState());

    try {
      if (!link) {
        throw new Error('Update playlist audio link not found');
      }

      await axios.request({
        url: link.href.replace('{playlistAudioGuid}', playlistAudioGuid),
        method: link.method,
        withCredentials: true,
        data: {
          note: note !== '' ? note : null,
          title,
        },
      });

      if (note !== undefined) {
        dispatch(
          addAlertAction({
            message: 'Note saved',
          })
        );
      }
      if (title !== undefined) {
        dispatch(
          addAlertAction({
            message: 'Title saved',
          })
        );
      }
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to update note. Please try again',
        })
      );
      throw e;
    }
  }
);

export const uploadPlaylistImageEffect = createAsyncThunk(
  'playlists/uploadPlaylistImage',
  async (data: UploadImageRequestData, { dispatch, getState }) => {
    const link = getUploadPlaylistImageLink(getState())?.href;
    try {
      if (!link) {
        throw new Error('Upload playlist image link not found');
      }
      const { uploadStatusLink } = await uploadImageHandler(link, data);
      return uploadStatusLink;
    } catch (e: any) {
      if (!axios.isCancel(e)) {
        let message = 'Unexpected error, please try again';
        const dimensionError = e.response?.data?.error?.details?.find(
          (el) => el?.errorType === 'minimum'
        );

        if (dimensionError) {
          message = `Crop selection is to small, please select a larger area`;
        }

        dispatch(addAlertAction({ message }));
      }
      throw e;
    }
  }
);

export const fetchPlaylistImageStatusEffect = createAsyncThunk(
  'playlists/fetchProfileImageStatus',
  async (_, { dispatch, getState }) => {
    const link = getPlaylistImageUploadStatusLink(getState())?.href;
    let playlist: Playlist | null = null;
    try {
      if (!link) {
        throw new Error('Playlist image status link not found');
      }
      const response = await axios.get<FetchUploadImageStatusResponse>(link, {
        withCredentials: true,
      });

      const { status } = response.data.response.image.item;
      const playlistLink = getState().playlists?.playlist?._links?.getPlaylist;
      if (status === ImageStatusType.SUCCESS && playlistLink) {
        const response = await axios.request<GetPlaylistResponse>({
          url: playlistLink.href,
          method: playlistLink.method,
          withCredentials: true,
        });

        playlist = response.data.response.playlist.item;
      }
      return { status, playlist };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const deletePlaylistImageStatusEffect = createAsyncThunk(
  'playlists/deletePlaylistImageStatus',
  async (playlistGuid: string, { dispatch, getState }) => {
    const link = getDeletePlaylistImageLink(getState());

    try {
      if (!link) {
        throw new Error('Playlist delete image link not found');
      }
      await axios.request({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);
